import {
  ChatCircleDotsIcon,
  CheckCircleIcon,
  PauseCircleIcon,
  PencilSimpleIcon,
  StopCircleIcon,
  WarningCircleIcon,
} from '@phosphor-icons/react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { statusKey, type NegotiationStatus } from '@entities/negotiation';
import { Badge, type BadgeTone } from '@shared/ui';

/**
 * Тон и иконка статуса переписки (DESIGN_SYSTEM §2.3, §2.4, §5.8).
 *
 * Цвет никогда не единственный носитель смысла: в капсуле всегда иконка и
 * подпись. Роли цветов не смешиваются:
 *
 * * «идёт» — синий акцент: это активное состояние, а не оценка (§2.3);
 * * «ваше решение» — оранжевый «требует внимания» (§2.4);
 * * «завершено», «остановлено», «пауза», «черновик» — нейтральные: исход
 *   переписки не соответствие критерию, и зелёным он не красится — зелёный
 *   значит только «соответствует» (§2.6). Различают их иконка и текст.
 */
const STATUS: Partial<Record<string, { tone: BadgeTone; icon: ReactNode }>> = {
  draft: { tone: 'neutral', icon: <PencilSimpleIcon weight="bold" /> },
  active: { tone: 'action', icon: <ChatCircleDotsIcon weight="bold" /> },
  awaiting_user: { tone: 'attention', icon: <WarningCircleIcon weight="bold" /> },
  paused: { tone: 'neutral', icon: <PauseCircleIcon weight="bold" /> },
  completed: { tone: 'neutral', icon: <CheckCircleIcon weight="bold" /> },
  closed: { tone: 'neutral', icon: <StopCircleIcon weight="bold" /> },
};

interface NegotiationStatusBadgeProps {
  status: NegotiationStatus;
}

/** Статус переписки — капсула с иконкой и текстом (список и карточка переписки). */
export function NegotiationStatusBadge({ status }: NegotiationStatusBadgeProps) {
  const { t } = useTranslation();
  // Статус, которого фронт ещё не знает, рисуется нейтральным: цвет не
  // выдумывается под код, о смысле которого экран не осведомлён.
  const { tone, icon } = STATUS[status] ?? { tone: 'neutral', icon: undefined };

  return (
    <Badge tone={tone} icon={icon}>
      {t(statusKey(status))}
    </Badge>
  );
}
